import React from 'react';
import {useTranslation} from 'react-i18next';

import {Breadcrumbs, IconMenu, ItemsType} from '@veneer/core';

import {useMediaQuery} from '../../hooks';
import {AppSwitcher} from '../AppSwitcher';
import {AppSwitcherProps} from '../WFCAppSwitcher';
import {WFCButton} from '../WFCButton';
import {UserMenu} from '../src/UserMenu';
import {UserInfo} from '../src/UserMenu/types';

import Actions from './components/Actions/Actions';
import {ActionsProps} from './components/Actions/types';
import useStyle from './styles';

export type AppHeaderProps = {
    toggleSideMenu: () => void;
    userInfo?: UserInfo | null;
    actions: ActionsProps;
    breadcrumbs?: ItemsType[];
    homeUrl?: string;
    environment: AppSwitcherProps['environment'];
    disableAppSwitcher: boolean;
    headerActions?: React.ReactNode;
    hasSideMenu: boolean;
    loginType: AppSwitcherProps['loginType'];
};

const AppHeader = ({
    hasSideMenu,
    breadcrumbs = [],
    userInfo,
    homeUrl,
    environment,
    disableAppSwitcher,
    headerActions,
    actions,
    toggleSideMenu,
    loginType,
}: AppHeaderProps) => {
    const {t} = useTranslation(['AppHeader']);
    const style = useStyle();

    const crumbs = breadcrumbs.map(item => ({label: item.text, href: item.url}));

    const isLgScreen = useMediaQuery('lg');

    return (
        <div data-testid="AppHeader">
            <AppSwitcher
                isHPEmail={userInfo?.email?.split('@')[1] === 'hp.com'}
                environment={environment}
                homeUrl={homeUrl}
                disabled={disableAppSwitcher}
                loginType={loginType}
                breadcrumbs={isLgScreen ? crumbs : []}
                trailing={
                    <div className={style.trailingStyle}>
                        {headerActions}
                        {isLgScreen && <Actions {...actions} />}
                        {userInfo && (
                            <UserMenu
                                userInfo={userInfo}
                                actions={actions.userActions}
                            />
                        )}
                    </div>
                }
            />
            {!isLgScreen && (
                <div
                    className={style.mobileHeader}
                    data-testid="small-screen-header"
                >
                    <div className={style.trailingStyle}>
                        {hasSideMenu && (
                            <WFCButton
                                onClick={toggleSideMenu}
                                leadingIcon={<IconMenu />}
                                aria-label={t('AppHeader:sideMenuToggle', 'Toggle Side Menu')}
                            />
                        )}
                        {breadcrumbs && (
                            <Breadcrumbs
                                i18n={{breadcrumbs: t('AppHeader:breadcrumbs', 'Breadcrumbs')}}
                                items={breadcrumbs}
                            />
                        )}
                    </div>
                    <div className={style.trailingStyle}>
                        <Actions {...actions} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default AppHeader;
